import { useClientPortalAPI } from "@certn/api";
import { Container, Layout, Text } from "@certn/cascadia-react";
import { Skeleton } from "@certn/cascadia-react/patterns/skeleton/Skeleton";
import { useTimeTracking } from "@certn/utils";
import { css } from "@emotion/css";
import Cookies from "js-cookie";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { CredentialsMissingBanner } from "./CredentialsMissingBanner";
import { DashboardTasks } from "./DashboardTasks";
import { OverdueBillingBanner } from "./OverdueBillingBanner";
import { PersonalisedPackages } from "./PersonalisedPackages";
import { TermsAndConditionsModal } from "./TermsAndConditionsModal";
import { TermsAndConditionsBanner } from "./TermsAndConditionsBanner";
import { AppFooterLinks } from "../../components/app-footer/AppFooterLinks";
import { AppHeader } from "../../components/app-header/AppHeader";
import { AppNavigation } from "../../components/app-navigation/AppNavigation";
import { DashboardSummary } from "../../components/dashboard-main/DashboardSummary";
import { DashboardSummaryItem } from "../../components/dashboard-main/DashboardSummaryItem";
import { useUserFeatures } from "../../components/user-feature/UserFeatureProvider";
import { useUser } from "../../hooks/useUser";
import { CaseCounts } from "../../types";
import { EmailVerificationBanner } from "./EmailVerificationBanner";
import { MonitoringEligibilityBanner } from "./MonitoringEligibilityBanner";

export function DashboardPage() {
  const { clientPortalPersonalisedPackagesBanner } = useFlags();
  const { t } = useTranslation();
  const { details: userDetails, isLoading } = useUser();
  const userName = userDetails?.auth0_full_name;
  const navigate = useNavigate();
  const location = useLocation();
  const [caseCounts, setCaseCounts] = useState<CaseCounts | null>(null);
  const [customerGroupId, setCustomerGroupId] = useState<string | null>(null);
  const [showTermsAndConditionsModal, setShowTermsAndConditionsModal] =
    useState(false);
  const [termsRequired, setTermsRequired] = useState(false);
  const { features } = useUserFeatures();
  const { cancelTimeTracker } = useTimeTracking();
  const { clientPortalRequireEmailVerification } = useFlags();

  const { data } = useClientPortalAPI(
    customerGroupId
      ? `/api/internal/client-portal/customer-accounts/${customerGroupId}/check-credentials/`
      : null
  );
  const { data: terms } = useClientPortalAPI(
    customerGroupId
      ? `/api/internal/client-portal/customer-accounts/${customerGroupId}/check-terms-and-conditions/`
      : null
  );
  const { data: billingNotices } = useClientPortalAPI(
    "/api/internal/client-portal/customer-accounts/billing-notices/"
  );

  const handleDataChange = (data: {
    caseCounts: CaseCounts | null;
    customerGroupId: string | null;
  }) => {
    setCaseCounts(data.caseCounts);
    setCustomerGroupId(data.customerGroupId);
  };

  useEffect(() => {
    cancelTimeTracker("order--order-complete");
    cancelTimeTracker("order--review-step");
    cancelTimeTracker("order--check-selection-step");
    cancelTimeTracker("order--order-details-step");
  }, [cancelTimeTracker]);

  useEffect(() => {
    Cookies.set("last_known_email_used", userDetails?.auth0_email || "", {
      expires: 365,
      sameSite: "None",
      secure: true,
      domain: "certn.co",
    });

    Cookies.set("last_known_name", userDetails?.auth0_full_name || "", {
      expires: 365,
      sameSite: "None",
      secure: true,
      domain: "certn.co",
    });

    Cookies.set("last_logged_domain", "Certn Centric", {
      expires: 365,
      sameSite: "None",
      secure: true,
      domain: "certn.co",
    });

    Cookies.set("last_logged_in", new Date().toISOString(), {
      expires: 365,
      sameSite: "None",
      secure: true,
      domain: "certn.co",
    });
  }, [userDetails?.auth0_email, userDetails?.auth0_full_name]);

  useEffect(() => {
    if (terms?.required) {
      setTermsRequired(true);
      setShowTermsAndConditionsModal(true);
    }
  }, [terms?.required]);

  return (
    <Layout
      layout="wide"
      header={<AppHeader />}
      navigation={<AppNavigation />}
      footer={<AppFooterLinks />}
      backgroundColor="surface-sunken"
    >
      <Container>
        {isLoading ? (
          <Skeleton textStyle="global/title-large" width={400} />
        ) : (
          <Text as="h1" style="global/title-large">
            {userName ? `${userName}'s ${t("home")}` : t("Home")}
          </Text>
        )}
        {clientPortalRequireEmailVerification && <EmailVerificationBanner />}
        {data?.show_banner && <CredentialsMissingBanner />}
        {(billingNotices?.has_overdue_invoices ||
          billingNotices?.has_blocking_invoices) && (
          <OverdueBillingBanner
            overdueProfiles={billingNotices.overdue_profiles}
            blockingProfiles={billingNotices.blocking_profiles}
          />
        )}
        {termsRequired && (
          <TermsAndConditionsBanner
            onAcceptTerms={() => setShowTermsAndConditionsModal(true)}
          />
        )}
        {clientPortalPersonalisedPackagesBanner &&
        features["PERSONALIZED_PACKAGES"] ? (
          <PersonalisedPackages />
        ) : null}
        <MonitoringEligibilityBanner />
        <DashboardSummary
          testID="dashboard-summary"
          className={styles.card}
          onDataChange={handleDataChange}
        >
          <DashboardSummaryItem
            label={t("Active")}
            number={caseCounts ? String(caseCounts.active) : "-"}
            testID="dashboard-summary-active-button"
            onPress={() =>
              navigate(`/cases?customer_group_id=${customerGroupId}`)
            }
          />
          <DashboardSummaryItem
            label={t("Review")}
            number={caseCounts ? String(caseCounts.review) : "-"}
            testID="dashboard-summary-review-button"
            onPress={() =>
              navigate(`/cases/review?customer_group_id=${customerGroupId}`)
            }
          />
          <DashboardSummaryItem
            label={t("Complete")}
            number={caseCounts ? String(caseCounts.complete) : "-"}
            testID="dashboard-summary-complete-button"
            onPress={() =>
              navigate(`/cases/done?customer_group_id=${customerGroupId}`)
            }
          />
        </DashboardSummary>
        <DashboardTasks
          title={t("My tasks")}
          testID="my-tasks"
          className={styles.card}
          onRowPress={(task) => {
            navigate(`/case-details/${task.case?.short_id}`, {
              state: {
                last: {
                  href: `${location.pathname}${location.search}`,
                },
              },
            });
          }}
        />
        {showTermsAndConditionsModal && (
          <TermsAndConditionsModal
            onClose={() => setShowTermsAndConditionsModal(false)}
            onSuccess={() => {
              setTermsRequired(false);
              setShowTermsAndConditionsModal(false);
            }}
            customerGroupId={customerGroupId}
          />
        )}
      </Container>
    </Layout>
  );
}
const styles = {
  card: css({
    boxShadow: "var(--shadow-card)",
  }),
};
